/**
 * シグネチャ商品プロファイル (スープ・タレ・香味油・主食材等の組合せ) 一覧画面。
 * 詳細設計: docs/design/v02-signature-product-profile-design.md §5-2
 *
 * AIメニュー開発チャットの system prompt へ、選択されたプロファイルの構造化データが
 * 前提条件として注入される (apps/api/src/lib/anthropic/contexts/menu-dev.ts 参照)。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, Copy, Trash2, ChefHat, Store as StoreIcon } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { ConfirmDialog, Modal } from '@/components/Modal';

type Store = { store_id: string; store_name: string; status: 'active' | 'closed' };
type Profile = {
  profile_id: string;
  profile_type: string;
  profile_name: string;
  brand_concept: string | null;
  target_customer: string | null;
  status: 'draft' | 'active';
  stores: Array<{ store_id: string; store_name: string }>;
  component_count: number;
  created_at: string;
  updated_at: string;
};
type CreateForm = { profile_name: string; store_ids: string[] };
type DuplicateForm = { new_profile_name: string; store_ids: string[] };

export function SignatureProductProfilesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: stores = [] } = useQuery<Store[]>({ queryKey: ['stores'], queryFn: () => apiFetch<Store[]>('/stores') });
  const { data: profiles = [], isLoading } = useQuery<Profile[]>({
    queryKey: ['signature-profiles'],
    // デザインレビュー反映 (2026-08-02): AI対話式作成を途中で離脱した下書きが一覧から完全に見えなくなり、
    // 再開する導線を失っていたため、下書きも含めて取得しバッジで区別する
    queryFn: () => apiFetch<Profile[]>('/menu-dev/profiles?include_draft=true'),
  });

  const [creating, setCreating] = useState<CreateForm | null>(null);
  const [duplicating, setDuplicating] = useState<{ profile: Profile; form: DuplicateForm } | null>(null);
  const [deleting, setDeleting] = useState<Profile | null>(null);

  const createMut = useMutation({
    mutationFn: (body: CreateForm) => apiFetch<{ profile_id: string }>('/menu-dev/profiles', {
      method: 'POST',
      body: JSON.stringify({ profile_name: body.profile_name.trim(), store_ids: body.store_ids }),
    }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      setCreating(null);
      toast.success('プロファイルを作成しました');
      navigate(`/ai/menu-profiles/${data.profile_id}`);
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '作成に失敗しました'),
  });

  const startBuilderMut = useMutation({
    mutationFn: () => apiFetch<{ session_id: string }>('/menu-chat/sessions', {
      method: 'POST',
      body: JSON.stringify({ session_type: 'profile_builder' }),
    }),
    onSuccess: (data) => navigate(`/ai/menu-profiles/builder/${data.session_id}`),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : 'セッション作成に失敗しました'),
  });

  const duplicateMut = useMutation({
    mutationFn: (params: { profileId: string; body: DuplicateForm }) =>
      apiFetch<{ profile_id: string }>(`/menu-dev/profiles/${params.profileId}/duplicate`, {
        method: 'POST',
        body: JSON.stringify({ new_profile_name: params.body.new_profile_name.trim(), store_ids: params.body.store_ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      setDuplicating(null);
      toast.success('プロファイルを複製しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '複製に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/menu-dev/profiles/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-profiles'] });
      setDeleting(null);
      toast.success('プロファイルを削除しました');
    },
    onError: (e) => { toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました'); setDeleting(null); },
  });

  const toggleStore = (list: string[], storeId: string): string[] =>
    list.includes(storeId) ? list.filter((id) => id !== storeId) : [...list, storeId];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-h1 flex items-center gap-2"><ChefHat size={22} /> シグネチャ商品プロファイル</h1>
          <p className="text-sm text-neutral-sub mt-1">
            スープ・タレ・香味油・主食材等の組合せを店舗ごとに登録します。AIメニュー開発でプロファイルを選択すると、
            登録内容を前提条件として提案が生成されます。
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => startBuilderMut.mutate()}
            disabled={startBuilderMut.isPending}
            className="inline-flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm font-medium whitespace-nowrap disabled:opacity-50"
          >
            <Sparkles size={16} /> AIとの対話で作成
          </button>
          <button
            type="button"
            onClick={() => setCreating({ profile_name: '', store_ids: [] })}
            className="inline-flex items-center gap-1 px-3 py-2 rounded border border-neutral-line text-sm font-medium whitespace-nowrap hover:bg-neutral-panel"
          >
            <Plus size={16} /> 空フォームから作成
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-neutral-sub">読込中…</div>
      ) : profiles.length === 0 ? (
        <EmptyState
          message="プロファイルが登録されていません"
          description="「AIとの対話で作成」から、店舗のスープ等の特徴を会話しながら登録できます"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <div key={p.profile_id} className="p-5 rounded-xl border border-zinc-100 bg-white shadow-sm space-y-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={() => navigate(`/ai/menu-profiles/${p.profile_id}`)}
                    className="text-[15px] font-semibold text-brand-black hover:text-brand-red text-left"
                  >
                    {p.profile_name}
                  </button>
                  {p.status === 'draft' && (
                    <span className="text-[11px] font-normal px-2 py-0.5 rounded-full bg-warning-bg text-warning-text border border-warning-border shrink-0">作成中</span>
                  )}
                </div>
                {p.brand_concept && <div className="text-sm text-zinc-500 mt-1 line-clamp-2">{p.brand_concept}</div>}
              </div>
              <div className="flex items-center gap-1 text-xs text-zinc-500 flex-wrap">
                <StoreIcon size={12} />
                {p.stores.length > 0 ? p.stores.map((s) => s.store_name).join('、') : '未紐付け'}
              </div>
              <div className="text-xs text-zinc-400">構成要素 {p.component_count} 件</div>
              <div className="flex justify-end gap-2 pt-1 border-t border-zinc-50">
                <button
                  type="button"
                  onClick={() => setDuplicating({ profile: p, form: { new_profile_name: `${p.profile_name} のコピー`, store_ids: [] } })}
                  className="p-1.5 text-neutral-sub hover:text-brand-red" aria-label="複製"
                >
                  <Copy size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleting(p)}
                  className="p-1.5 text-neutral-sub hover:text-warning-text" aria-label="削除"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={creating !== null}
        title="新規プロファイル (空フォーム)"
        onClose={() => setCreating(null)}
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setCreating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button
              type="button"
              disabled={createMut.isPending || !creating?.profile_name.trim()}
              onClick={() => creating && createMut.mutate(creating)}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
            >
              {createMut.isPending ? '作成中…' : '作成して構成要素を編集'}
            </button>
          </>
        }
      >
        {creating && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">プロファイル名*</label>
              <input autoFocus value={creating.profile_name}
                onChange={(e) => setCreating({ ...creating, profile_name: e.target.value })}
                placeholder="例：定番醤油豚骨"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">適用店舗</label>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-neutral-line rounded p-2">
                {stores.filter((s) => s.status === 'active').map((s) => (
                  <label key={s.store_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={creating.store_ids.includes(s.store_id)}
                      onChange={() => setCreating({ ...creating, store_ids: toggleStore(creating.store_ids, s.store_id) })} />
                    {s.store_name}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={duplicating !== null}
        title="プロファイルを複製"
        onClose={() => setDuplicating(null)}
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setDuplicating(null)} className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button
              type="button"
              disabled={duplicateMut.isPending || !duplicating?.form.new_profile_name.trim()}
              onClick={() => duplicating && duplicateMut.mutate({ profileId: duplicating.profile.profile_id, body: duplicating.form })}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50"
            >
              {duplicateMut.isPending ? '複製中…' : '複製する'}
            </button>
          </>
        }
      >
        {duplicating && (
          <div className="space-y-3">
            <p className="text-sm text-neutral-sub">「{duplicating.profile.profile_name}」の構成要素をすべてコピーした新しいプロファイルを作成します。</p>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">新しいプロファイル名*</label>
              <input autoFocus value={duplicating.form.new_profile_name}
                onChange={(e) => setDuplicating({ ...duplicating, form: { ...duplicating.form, new_profile_name: e.target.value } })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">適用店舗</label>
              <div className="space-y-1 max-h-40 overflow-y-auto border border-neutral-line rounded p-2">
                {stores.filter((s) => s.status === 'active').map((s) => (
                  <label key={s.store_id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={duplicating.form.store_ids.includes(s.store_id)}
                      onChange={() => setDuplicating({ ...duplicating, form: { ...duplicating.form, store_ids: toggleStore(duplicating.form.store_ids, s.store_id) } })} />
                    {s.store_name}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="プロファイルを削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.profile_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>「{deleting.profile_name}」を削除します。登録済みの構成要素 ({deleting.component_count} 件) もすべて削除され、<strong className="text-danger-text">この操作は取り消せません</strong>。</p>
          </div>
        )}
      />
    </div>
  );
}
